import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/PermissionGate';
import {
  Sparkles, Loader2, Send, History, RotateCw, MessageSquare, ChevronDown, ChevronUp, User, Bot,
  Bookmark, Pin, Trash2,
} from 'lucide-react';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F',
  slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)',
  border: 'rgba(30,78,140,0.14)', surface: 'var(--card-bg)', surfaceAlt: 'var(--card-bg-alt)',
};

const PHASE_TO_ROUTE = {
  PRE_ORDER: 'pre-order', POST_ORDER: 'post-order', DELIVERY_FORECAST: 'delivery-forecast',
};

const mdComponents = {
  h1: (p) => <h3 className="text-[12px] font-bold mt-2 mb-1 pb-0.5" style={{ color: C.primaryText, borderBottom: `1px solid ${C.border}` }} {...p} />,
  h2: (p) => <h4 className="text-[11px] font-bold mt-2 mb-1 pb-0.5" style={{ color: C.primaryText, borderBottom: `1px solid ${C.border}` }} {...p} />,
  h3: (p) => <h5 className="text-[10px] font-bold uppercase mt-1.5 mb-1" style={{ color: C.slateMuted }} {...p} />,
  p:  (p) => <p className="text-[11px] my-1 leading-snug" {...p} />,
  ul: (p) => <ul className="text-[11px] my-1 space-y-0.5 list-disc pl-4" {...p} />,
  ol: (p) => <ol className="text-[11px] my-1 space-y-0.5 list-decimal pl-4" {...p} />,
  li: (p) => <li {...p} />,
  strong: (p) => <strong className="font-semibold" style={{ color: C.accent }} {...p} />,
  code: (p) => <code className="text-[10px] px-1 py-0.5 rounded" style={{ background: C.surfaceAlt, color: C.primaryText }} {...p} />,
  a:  (p) => <a className="underline" style={{ color: C.primaryText }} {...p} />,
};

/**
 * Reusable Cognara AI Analyst panel — pinned to a phase
 * (PRE_ORDER | POST_ORDER | DELIVERY_FORECAST).
 * Features:
 *  - Run / Regenerate with fresh data
 *  - History picker (previous generations for this phase)
 *  - Chat pinned to latest insight (multi-turn)
 *  - Full markdown formatting
 */
// The caller's accent doubles as a button background, so only the heading is lifted.
const TONE_TEXT_IP = { '#1E4E8C': 'var(--tone-navy-text)' };

export default function InsightsPanel({ projectId, phase, color = C.primary, testId = 'ct-insights' }) {
  const [insights, setInsights] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [chat, setChat] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [msg, setMsg] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [threads, setThreads] = useState([]);
  const [showThreads, setShowThreads] = useState(false);
  const [savingThread, setSavingThread] = useState(false);
  const scrollRef = useRef(null);

  const route = PHASE_TO_ROUTE[phase];

  const loadLatest = async () => {
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/mci/control-tower/${route}/insights/latest`);
      if (data && data.narrative) setInsights(data);
    } catch (e) { /* ignore */ }
  };
  const loadHistory = async () => {
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/mci/control-tower/insights/history?phase=${phase}&limit=10`);
      setHistory(data.history || []);
    } catch (e) { /* ignore */ }
  };
  const loadChat = async () => {
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/mci/control-tower/insights/chat?phase=${phase}&limit=20`);
      const flat = [];
      (data.chat || []).forEach(t => {
        flat.push({ role: 'user', content: t.question, ts: t.ts });
        flat.push({ role: 'assistant', content: t.answer, ts: t.ts, cognaraUsed: t.cognaraUsed });
      });
      setChat(flat);
    } catch (e) { /* ignore */ }
  };
  const loadThreads = async () => {
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/mci/control-tower/insights/chat-threads?phase=${phase}`);
      setThreads(data.threads || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (projectId && phase) { loadLatest(); loadHistory(); loadChat(); loadThreads(); }
    // eslint-disable-next-line
  }, [projectId, phase]);

  const saveThread = async () => {
    if (chat.length === 0) return toast.info('No chat to save');
    const name = window.prompt('Name this chat thread', `${phase.replace('_', ' ')} · ${new Date().toLocaleDateString()}`);
    if (!name) return;
    setSavingThread(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mci/control-tower/insights/chat-threads`,
        { phase, name, messages: chat.map(m => ({ role: m.role, content: m.content })),
          pinToDPR: false, pinToWPR: false });
      toast.success(`Thread "${data.name}" saved`);
      loadThreads();
    } catch (e) { toast.error('Save thread failed'); }
    setSavingThread(false);
  };

  const togglePin = async (thread, target) => {
    const key = target === 'DPR' ? 'pinToDPR' : 'pinToWPR';
    try {
      await api.post(
        `/v1/projects/${projectId}/mci/control-tower/insights/chat-threads/${thread.id}/pin`,
        { [key]: !thread[key] });
      toast.success(`${target} ${thread[key] ? 'unpinned' : 'pinned'}`);
      loadThreads();
    } catch (e) { toast.error('Pin failed'); }
  };

  const deleteThread = async (thread) => {
    if (!window.confirm(`Delete thread "${thread.name}"?`)) return;
    try {
      await api.delete(
        `/v1/projects/${projectId}/mci/control-tower/insights/chat-threads/${thread.id}`);
      toast.success('Deleted');
      loadThreads();
    } catch (e) { toast.error('Delete failed'); }
  };

  const openThread = (t) => {
    setChat((t.messages || []).map(m => ({ role: m.role, content: m.content })));
    setShowThreads(false);
    setShowChat(true);
  };

  const runInsights = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mci/control-tower/${route}/insights`);
      setInsights(data);
      loadHistory();
      toast.success(`Cognara ${insights ? 'regenerated' : 'ran'} · ${data.cognaraUsed ? 'live' : 'deterministic'}`);
    } catch (e) {
      toast.error('Cognara run failed');
    }
    setBusy(false);
  };

  const sendChat = async () => {
    const text = msg.trim();
    if (!text) return;
    setMsg('');
    const nextChat = [...chat, { role: 'user', content: text }];
    setChat(nextChat);
    setChatBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mci/control-tower/insights/chat`,
        { phase, messages: nextChat.map(m => ({ role: m.role, content: m.content })) });
      setChat([...nextChat, { role: 'assistant', content: data.reply, cognaraUsed: data.cognaraUsed }]);
      setTimeout(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, 60);
    } catch (e) {
      toast.error('Chat failed');
    }
    setChatBusy(false);
  };

  return (
    <div className="rounded-lg p-3 sticky top-4" style={{ background: C.surface, border: `1px solid ${C.border}` }}
      data-testid={testId}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.08em] font-semibold flex items-center gap-1" style={{ color: TONE_TEXT_IP[color] || color }}>
          <Sparkles className="w-3 h-3" /> Cognara AI Analyst
        </div>
        <div className="flex items-center gap-1">
          <button className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5"
            style={{ color: C.slateMuted, background: showHistory ? C.surfaceAlt : 'transparent' }}
            onClick={() => setShowHistory(v => !v)} data-testid="cognara-history-btn" title="History">
            <History className="w-3 h-3" /> {history.length}
          </button>
          <PermissionGate module="MCI" action="ai">
            <Button size="sm" onClick={runInsights} disabled={busy}
              className="h-6 px-2 text-[10px] font-semibold text-white" style={{ background: color }}
              data-testid="cognara-run-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> :
                insights ? <><RotateCw className="w-3 h-3 mr-0.5 inline" /> Regenerate</> : 'Run'}
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Header meta */}
      {insights?.generatedAt && (
        <div className="text-[9px] mb-1 flex items-center gap-1" style={{ color: C.slateMuted }}>
          <span>{new Date(insights.generatedAt).toLocaleString()}</span>
          <span>·</span>
          <span className="px-1 rounded text-[9px]"
            style={{ background: insights.cognaraUsed ? 'var(--tone-blue-bg)' : 'var(--tone-slate-bg)',
                      color: insights.cognaraUsed ? 'var(--tone-blue-text)' : C.slateMuted }}>
            {insights.cognaraUsed ? 'Cognara live' : 'Cognara fallback'}
          </span>
        </div>
      )}

      {/* History dropdown */}
      {showHistory && history.length > 0 && (
        <div className="mb-2 rounded border max-h-40 overflow-y-auto" style={{ borderColor: C.border, background: C.surfaceAlt }}
          data-testid="cognara-history-list">
          {history.map((h, i) => (
            <button key={i} className="w-full text-left px-2 py-1 text-[10px] hover:opacity-80"
              onClick={() => { setInsights(h); setShowHistory(false); }}
              style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none', color: C.slate }}
              data-testid={`history-item-${i}`}>
              <span className="font-mono">{new Date(h.generatedAt).toLocaleString()}</span>
              <span className="ml-2 text-[9px]" style={{ color: C.slateMuted }}>
                {h.cognaraUsed ? 'live' : 'fallback'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Narrative */}
      <div className="text-[11px] mb-2 max-h-[420px] overflow-y-auto pr-1" style={{ color: C.slate }}>
        {insights?.narrative ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {insights.narrative}
          </ReactMarkdown>
        ) : (
          <div className="text-[10px] text-center py-3" style={{ color: C.slateMuted }}>
            Click Run to generate Cognara insights.
          </div>
        )}
      </div>

      {/* Chat toggle */}
      {insights?.narrative && (
        <div className="flex items-center gap-1">
          <button className="flex-1 flex items-center justify-between text-[10px] px-2 py-1 rounded border"
            style={{ borderColor: C.border, background: C.surfaceAlt, color: C.primaryText }}
            onClick={() => setShowChat(v => !v)} data-testid="cognara-chat-toggle">
            <span className="flex items-center gap-1 font-semibold">
              <MessageSquare className="w-3 h-3" /> Ask Cognara — {chat.length}
            </span>
            {showChat ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <button className="text-[10px] px-1.5 py-1 rounded border"
            style={{ borderColor: C.border, background: showThreads ? C.surfaceAlt : 'transparent', color: C.primaryText }}
            onClick={() => setShowThreads(v => !v)}
            data-testid="cognara-threads-toggle" title="Saved threads">
            <Bookmark className="w-3 h-3" /> {threads.length}
          </button>
        </div>
      )}

      {showThreads && (
        <div className="mt-2 rounded border max-h-56 overflow-y-auto"
          style={{ borderColor: C.border, background: C.surfaceAlt }}
          data-testid="cognara-threads-list">
          {threads.length === 0 ? (
            <div className="p-2 text-[10px] text-center" style={{ color: C.slateMuted }}>
              No saved threads. Open a chat, then click "Save Thread".
            </div>
          ) : threads.map((t) => (
            <div key={t.id} className="p-2 text-[10px]"
              style={{ borderTop: `1px solid ${C.border}` }}
              data-testid={`thread-${t.id}`}>
              <div className="flex items-center justify-between">
                <button onClick={() => openThread(t)} className="text-left flex-1 hover:underline"
                  style={{ color: C.primaryText, fontWeight: 600 }}>
                  {t.name}
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={() => togglePin(t, 'DPR')}
                    className="px-1 py-0.5 rounded text-[9px] font-bold"
                    style={{ background: t.pinToDPR ? C.primaryText : 'transparent',
                              color: t.pinToDPR ? '#fff' : C.slateMuted,
                              border: `1px solid ${C.border}` }}
                    data-testid={`pin-dpr-${t.id}`}>
                    <Pin className="w-2.5 h-2.5 inline" /> DPR
                  </button>
                  <button onClick={() => togglePin(t, 'WPR')}
                    className="px-1 py-0.5 rounded text-[9px] font-bold"
                    style={{ background: t.pinToWPR ? C.accent : 'transparent',
                              color: t.pinToWPR ? '#fff' : C.slateMuted,
                              border: `1px solid ${C.border}` }}
                    data-testid={`pin-wpr-${t.id}`}>
                    <Pin className="w-2.5 h-2.5 inline" /> WPR
                  </button>
                  <button onClick={() => deleteThread(t)} data-testid={`delete-${t.id}`}>
                    <Trash2 className="w-3 h-3" style={{ color: '#EF4444' }} />
                  </button>
                </div>
              </div>
              <div className="text-[9px]" style={{ color: C.slateMuted }}>
                {(t.messages || []).length} msg · {new Date(t.updatedAt || t.createdAt).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {showChat && (
        <div className="mt-2 rounded border" style={{ borderColor: C.border }} data-testid="cognara-chat-panel">
          <div ref={scrollRef} className="max-h-56 overflow-y-auto p-2 space-y-2 text-[11px]" style={{ background: C.surfaceAlt }}>
            {chat.length === 0 ? (
              <div className="text-[10px] text-center py-3" style={{ color: C.slateMuted }}>
                Ask a follow-up question about the analysis above.
              </div>
            ) : chat.map((m, i) => (
              <div key={i} className="flex gap-1.5" data-testid={`chat-msg-${i}`}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: m.role === 'user' ? C.primaryText : C.accent, color: '#fff' }}>
                  {m.role === 'user' ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                </div>
                <div className="flex-1 rounded p-1.5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                  {m.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {m.content}
                    </ReactMarkdown>
                  ) : (
                    <div className="text-[11px]" style={{ color: C.slate }}>{m.content}</div>
                  )}
                </div>
              </div>
            ))}
            {chatBusy && (
              <div className="flex items-center gap-1 text-[10px]" style={{ color: C.slateMuted }}>
                <Loader2 className="w-3 h-3 animate-spin" /> Cognara is thinking…
              </div>
            )}
          </div>
          <div className="flex gap-1 p-1.5 border-t" style={{ borderColor: C.border, background: C.surface }}>
            <input value={msg} onChange={e => setMsg(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !chatBusy) sendChat(); }}
              placeholder="e.g. Which vendor should we escalate first?"
              className="flex-1 px-2 py-1 rounded border text-[11px]"
              style={{ borderColor: C.border, background: C.surfaceAlt, color: C.slate }}
              data-testid="cognara-chat-input" />
            <Button size="sm" onClick={sendChat} disabled={chatBusy || !msg.trim()}
              className="h-7 px-2 text-white" style={{ background: color }}
              data-testid="cognara-chat-send">
              <Send className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="outline" onClick={saveThread}
              disabled={savingThread || chat.length === 0}
              className="h-7 px-2 text-[10px]"
              data-testid="cognara-save-thread" title="Save as named thread">
              {savingThread ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
